import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Upload, ImagePlus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SHAPES = ["soft", "arch", "circle"];

const inputCls =
    "w-full border border-gold/30 bg-ivory/5 px-5 py-4 text-sm text-ivory placeholder:text-ivory/40 outline-none transition-[border-color,box-shadow] duration-500 focus:border-gold focus:shadow-[0_0_0_3px_rgba(201,162,39,0.18)]";

export const GalleryManager = ({ token }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [file, setFile] = useState(null);
    const [caption, setCaption] = useState("");
    const [shape, setShape] = useState("soft");
    const [uploading, setUploading] = useState(false);
    const headers = { Authorization: `Bearer ${token}` };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/gallery`);
            setItems(data);
        } catch {
            toast.error("Could not load gallery.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const upload = async (e) => {
        e.preventDefault();
        if (!file || !caption.trim()) {
            toast.error("Choose a photo and add a caption first.");
            return;
        }
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            form.append("caption", caption.trim());
            form.append("shape", shape);
            const { data } = await axios.post(`${API}/gallery`, form, { headers });
            setItems((list) => [...list, data]);
            setFile(null);
            setCaption("");
            setShape("soft");
            toast.success("Photo added to the gallery.");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not upload photo.");
        } finally {
            setUploading(false);
        }
    };

    const remove = async (img) => {
        if (!window.confirm(`Remove "${img.caption}" from the gallery?`)) return;
        try {
            await axios.delete(`${API}/gallery/${img.id}`, { headers });
            setItems((list) => list.filter((i) => i.id !== img.id));
            toast.success("Photo removed.");
        } catch {
            toast.error("Could not remove photo.");
        }
    };

    return (
        <div data-testid="gallery-manager">
            <form onSubmit={upload} data-testid="gallery-upload-form" className="border border-gold/20 bg-emerald/50 p-6 backdrop-blur-sm sm:p-8">
                <h2 className="font-serif text-xl text-ivory">Add a new photo</h2>
                <div className="mt-5 flex flex-col gap-4 sm:flex-row sm:items-end">
                    <div className="flex-1">
                        <label className="mb-2 block text-[10px] uppercase tracking-[0.3em] text-gold/80">Photo</label>
                        <input
                            data-testid="gallery-file-input"
                            type="file"
                            accept="image/*"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                            className={`${inputCls} py-3`}
                        />
                    </div>
                    <div className="flex-1">
                        <label className="mb-2 block text-[10px] uppercase tracking-[0.3em] text-gold/80">Caption</label>
                        <input
                            data-testid="gallery-caption-input"
                            type="text"
                            value={caption}
                            onChange={(e) => setCaption(e.target.value)}
                            placeholder="e.g. The Garden Toast"
                            className={inputCls}
                        />
                    </div>
                    <div>
                        <label className="mb-2 block text-[10px] uppercase tracking-[0.3em] text-gold/80">Frame</label>
                        <select
                            data-testid="gallery-shape-select"
                            value={shape}
                            onChange={(e) => setShape(e.target.value)}
                            className={`${inputCls} sm:w-40`}
                        >
                            {SHAPES.map((s) => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                    </div>
                    <button
                        type="submit"
                        data-testid="gallery-upload-btn"
                        disabled={uploading}
                        className="flex items-center justify-center gap-2 bg-gold px-6 py-4 text-[10px] font-semibold uppercase tracking-[0.3em] text-emerald-deep transition-opacity duration-300 disabled:opacity-60"
                    >
                        <Upload size={14} /> {uploading ? "Uploading…" : "Add Photo"}
                    </button>
                </div>
            </form>

            <div className="mt-8">
                {loading ? (
                    <p className="py-10 text-center text-sm text-ivory/60" data-testid="gallery-manager-loading">Loading gallery…</p>
                ) : items.length === 0 ? (
                    <div className="flex flex-col items-center gap-3 border border-dashed border-gold/25 py-16" data-testid="gallery-manager-empty">
                        <ImagePlus className="text-gold/50" size={32} strokeWidth={1.2} />
                        <p className="text-sm text-ivory/55">No photos yet — add your first one above.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="gallery-manager-grid">
                        {items.map((img) => (
                            <div key={img.id} data-testid={`gallery-manager-item-${img.id}`} className="group relative border border-gold/20 bg-emerald/40 p-2">
                                <img src={img.url} alt={img.caption} className="aspect-square w-full object-cover" />
                                <p className="mt-2 truncate text-center text-xs text-ivory/70">{img.caption}</p>
                                <p className="text-center text-[9px] uppercase tracking-[0.25em] text-gold/60">{img.shape}</p>
                                <button
                                    data-testid={`gallery-manager-delete-${img.id}`}
                                    onClick={() => remove(img)}
                                    aria-label={`Remove ${img.caption}`}
                                    className="absolute right-2 top-2 flex items-center gap-1 bg-blushdeep/90 px-2 py-1 text-[9px] font-semibold uppercase tracking-wider text-emerald-deep opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                                >
                                    <Trash2 size={11} /> Remove
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
